import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBlog } from "@/lib/api";
import { Clock, ArrowUpRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const FILTERS = [
  { key: "tous", label: "Tout" },
  { key: "cas", label: "Études de cas" },
  { key: "lean", label: "Lean" },
  { key: "neuro", label: "Neurosciences" },
  { key: "ia", label: "IA" },
];

const catLabel = { lean: "Lean", neuro: "Neuro", ia: "IA", cas: "Étude de cas" };

export default function BlogPage() {
  useDocumentTitle("Études & Blog", "Études de cas anonymisées, notes neuroscientifiques, retours d'expérience sur l'IA opérationnelle et le Lean Management. Le terrain, sans filtre.");
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState("tous");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlog(active === "tous" ? null : active)
      .then(setPosts)
      .finally(() => setLoading(false));
  }, [active]);

  return (
    <main className="pt-32 pb-24 bg-pearl min-h-screen" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="mb-16 max-w-3xl">
          <div className="font-mono-overline text-mute mb-3">Études & blog</div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] tracking-tight" data-testid="blog-title">
            Le terrain. <em className="text-mute not-italic font-light">Sans filtre.</em>
          </h1>
          <p className="mt-6 text-mute text-lg leading-relaxed">
            Études de cas anonymisées, notes neuroscientifiques, retours
            d'expérience sur l'IA opérationnelle.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="blog-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              data-active={active === f.key}
              data-testid={`blog-filter-${f.key}`}
              className="filter-btn"
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-mute" data-testid="blog-loading">Chargement…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.length === 0 && (
              <p className="text-mute col-span-3">Aucun article dans cette catégorie.</p>
            )}
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="block bg-white border border-ink/10 card-lift group"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-[5/3] overflow-hidden bg-pearl-dark">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3 text-xs">
                    <span className="font-mono-overline text-mute">{catLabel[p.category] || p.category}</span>
                    <span className="text-mute">·</span>
                    <span className="inline-flex items-center gap-1 text-mute">
                      <Clock size={12} /> {p.read_minutes} min
                    </span>
                  </div>
                  <h3 className="font-serif text-xl leading-tight mb-3">{p.title}</h3>
                  <p className="text-sm text-mute leading-relaxed line-clamp-3 mb-4">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-ink group-hover:gap-2 transition-all">
                    Lire l'article <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
